import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { apiService } from '../../../components/Config/api.service';
import { FaEdit, FaTrash, FaPlus, FaArrowLeft, FaArrowRight } from 'react-icons/fa';

/**
 * Types d'absence de la Paie (YRH-FOUND-018, Yros/Kavero#417).
 *
 * Restauré depuis `79faeed^`. Chaque type créé ici reçoit côté Odoo un type
 * de congé jumeau (payor_backend#401) : c'est ce qui le rend demandable par
 * les salariés dans « Demander un congé ». Il n'y a plus rien d'autre à
 * configurer.
 *
 * Création et modification passent par des routes Odoo `type='json'` que le
 * proxy relaie telles quelles : un refus y arrive en 200, dans
 * `result.status === 'error'`, et doit être traité comme un échec.
 */

// Rend le corps utile d'une réponse Odoo, ou lève son message d'erreur.
const lireReponseOdoo = (reponse) => {
    const donnees = reponse?.data;
    if (donnees?.error) {
        throw new Error(donnees.error.data?.message || donnees.error.message);
    }
    const corps = donnees?.result ?? donnees;
    if (corps?.status === 'error') {
        throw new Error(corps.message);
    }
    return corps;
};

const messageErreur = (err, repli) => err.response?.data?.message || err.message || repli;

const AbsenceType = ({ setIsDrawerOpen, refreshParent }) => {
    const { t } = useTranslation();
    const [absenceTypes, setAbsenceTypes] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [currentType, setCurrentType] = useState(null);
    const [pagination, setPagination] = useState({
        page: 1,
        limit: 10,
        total_pages: 1,
        total_count: 0
    });
    const [formData, setFormData] = useState({
        name: '',
        category: 'paid',
        description: ''
    });

    const categoryLabels = useMemo(() => ({
        paid: t('absenceType.paid'),
        unpaid: t('absenceType.unpaid'),
        deducted: t('absenceType.deducted'),
    }), [t]);

    const fetchAbsenceTypes = useCallback(async (page = 1) => {
        setIsLoading(true);
        setError(null);

        try {
            const params = new URLSearchParams();
            params.append('page', page);
            params.append('limit', pagination.limit);

            const response = await apiService.getElement(`/absence_types?${params.toString()}`);
            if (response.data && response.data.data) {
                setAbsenceTypes(response.data.data);
                if (response.data.pagination) {
                    setPagination(response.data.pagination);
                }
            } else {
                setError(t('absenceType.invalidResponseFormat'));
            }
        } catch (err) {
            setError(t('absenceType.fetchTypesErrorWithMessage', {
                message: messageErreur(err, t('absenceType.unknownError')),
            }));
        } finally {
            setIsLoading(false);
        }
    }, [pagination.limit, t]);

    // Un seul chargement, à l'ouverture du tiroir : `fetchAbsenceTypes` change
    // d'identité avec `t`, et le rechargement suit chaque action, jamais un
    // rendu.
    useEffect(() => {
        fetchAbsenceTypes(1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleEditClick = useCallback((type) => {
        setCurrentType(type);
        setFormData({
            name: type.name,
            category: type.category,
            description: type.description || ''
        });
        setError(null);
        setIsEditing(true);
    }, []);

    const handleNewClick = useCallback(() => {
        setCurrentType(null);
        setFormData({
            name: '',
            category: 'paid',
            description: ''
        });
        setError(null);
        setIsEditing(true);
    }, []);

    const handleDeleteClick = useCallback(async (id) => {
        if (!window.confirm(t('absenceType.deleteConfirm'))) {
            return;
        }

        setIsLoading(true);
        setError(null);

        try {
            await apiService.deleteElement('/absence_types', id);
            await fetchAbsenceTypes(pagination.page);
            if (refreshParent) refreshParent();
        } catch (err) {
            setError(t('absenceType.deleteErrorWithMessage', {
                message: messageErreur(err, t('absenceType.unknownError')),
            }));
            setIsLoading(false);
        }
    }, [fetchAbsenceTypes, refreshParent, pagination.page, t]);

    const handleSubmit = async (e) => {
        if (e) e.preventDefault();

        if (!formData.name.trim()) {
            setError(t('absenceType.nameRequired'));
            return;
        }

        setIsLoading(true);
        setError(null);

        try {
            const reponse = currentType
                ? await apiService.updateElementById('/absence_types', currentType.id, formData)
                : await apiService.saveElement('/absence_types', formData);
            lireReponseOdoo(reponse);

            setIsEditing(false);
            await fetchAbsenceTypes(pagination.page);
            if (refreshParent) refreshParent();
        } catch (err) {
            setError(t('absenceType.saveErrorWithMessage', {
                message: messageErreur(err, t('absenceType.unknownError')),
            }));
            setIsLoading(false);
        }
    };

    const handleInputChange = useCallback((e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    }, []);

    const handleCancel = useCallback(() => {
        setIsEditing(false);
        setError(null);
    }, []);

    const renderForm = () => (
        <div className="max-w-3xl mx-auto rounded-lg">
            <form onSubmit={handleSubmit}>
                <h2 className="text-2xl font-semibold mb-6">
                    {currentType ? t('absenceType.editTitle') : t('absenceType.addTitle')}
                </h2>

                {error && <div className="alert alert-error mb-4">{error}</div>}

                <div className="mb-4">
                    <label htmlFor="absence-type-name" className="block text-sm font-medium mb-1">{t('absenceType.name')}</label>
                    <input
                        id="absence-type-name"
                        type="text"
                        name="name"
                        className="input input-bordered w-full"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                        disabled={isLoading}
                    />
                </div>

                <div className="mb-4">
                    <label htmlFor="absence-type-category" className="block text-sm font-medium mb-1">{t('absenceType.category')}</label>
                    <select
                        id="absence-type-category"
                        name="category"
                        className="select select-bordered w-full"
                        value={formData.category}
                        onChange={handleInputChange}
                        required
                        disabled={isLoading}
                    >
                        {Object.entries(categoryLabels).map(([value, label]) => (
                            <option key={value} value={value}>{label}</option>
                        ))}
                    </select>
                </div>

                <div className="mb-4">
                    <label htmlFor="absence-type-description" className="block text-sm font-medium mb-1">{t('absenceType.description')}</label>
                    <textarea
                        id="absence-type-description"
                        name="description"
                        className="textarea textarea-bordered w-full"
                        value={formData.description}
                        onChange={handleInputChange}
                        rows="3"
                        disabled={isLoading}
                    ></textarea>
                </div>

                <div className="flex justify-end gap-4 mt-6">
                    <button
                        type="button"
                        className="btn btn-outline"
                        onClick={handleCancel}
                        disabled={isLoading}
                    >
                        {t('basePrimeForm.cancel')}
                    </button>
                    <button
                        type="submit"
                        className="btn btn-outline bg-[--color-primary] text-white"
                        disabled={isLoading}
                    >
                        {isLoading ? t('absenceType.processing') : currentType ? t('hspForm.update') : t('absenceType.create')}
                    </button>
                </div>
            </form>
        </div>
    );

    const renderList = () => (
        <div className="pt-4 max-w-3xl mx-auto rounded-lg">
            <div className="flex justify-between items-center mb-2">
                <h2 className="text-2xl font-semibold">{t('absenceType.absenceTypes')}</h2>
                <button
                    className="btn rounded-full btn-outline bg-[--color-primary] text-white"
                    onClick={handleNewClick}
                    disabled={isLoading}
                >
                    <FaPlus className="mr-1" /> {t('absenceType.addNewType')}
                </button>
            </div>
            <p data-testid="types-absence-aide" className="text-sm text-gray-600 mb-6">
                {t('absenceType.aideDemandable')}
            </p>

            {error && <div className="alert alert-error mb-4">{error}</div>}

            {isLoading ? (
                <div className="flex justify-center items-center py-8">
                    <div className="loader">...</div>
                </div>
            ) : (
                <>
                    {absenceTypes.length === 0 ? (
                        <div className="text-center py-8 text-gray-500">
                            {t('absenceType.noTypesFound')}
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="table w-full">
                                <thead>
                                    <tr>
                                        <th>{t('absenceType.name')}</th>
                                        <th>{t('absenceType.category')}</th>
                                        <th>{t('absenceType.description')}</th>
                                        <th>{t('absenceType.actions')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {absenceTypes.map(type => (
                                        <tr key={type.id}>
                                            <td className="font-medium">{type.name}</td>
                                            <td>{categoryLabels[type.category] || type.category}</td>
                                            <td className="max-w-xs truncate">{type.description || '-'}</td>
                                            <td>
                                                <div className="flex gap-2">
                                                    <button
                                                        className="btn btn-xs btn-outline"
                                                        onClick={() => handleEditClick(type)}
                                                        aria-label={t('absenceType.edit')}
                                                        title={t('absenceType.edit')}
                                                    >
                                                        <FaEdit />
                                                    </button>
                                                    <button
                                                        className="btn btn-xs btn-outline btn-error"
                                                        onClick={() => handleDeleteClick(type.id)}
                                                        aria-label={t('hspForm.delete')}
                                                        title={t('hspForm.delete')}
                                                    >
                                                        <FaTrash />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {pagination.total_pages > 1 && (
                        <div className="mt-4 p-4 flex">
                            <div className="flex justify-center md:justify-between w-full">
                                <button
                                    className={`hidden md:flex btn btn-sm text-[--color-primary] hover:bg-[--color-primary] items-center px-4 rounded-md ${pagination.page === 1 ? 'cursor-not-allowed' : 'btn-outline'}`}
                                    onClick={() => pagination.page > 1 && fetchAbsenceTypes(pagination.page - 1)}
                                    disabled={pagination.page === 1 || isLoading}
                                >
                                    <FaArrowLeft /> {t('dailySalaryInputs.previous')}
                                </button>

                                <div className="flex gap-2">
                                    {Array.from({ length: pagination.total_pages }, (_, index) => (
                                        <button
                                            key={index + 1}
                                            className={`btn btn-sm rounded-md ${pagination.page === index + 1 ? 'bg-[--color-primary] text-white' : ''}`}
                                            onClick={() => fetchAbsenceTypes(index + 1)}
                                            disabled={isLoading}
                                        >
                                            {index + 1}
                                        </button>
                                    ))}
                                </div>

                                <button
                                    className={`hidden md:flex btn btn-sm text-[--color-primary] hover:bg-[--color-primary] items-center px-4 rounded-md ${pagination.page === pagination.total_pages ? 'cursor-not-allowed' : 'btn-outline'}`}
                                    onClick={() => pagination.page < pagination.total_pages && fetchAbsenceTypes(pagination.page + 1)}
                                    disabled={pagination.page === pagination.total_pages || isLoading}
                                >
                                    {t('dailySalaryInputs.next')} <FaArrowRight />
                                </button>
                            </div>
                        </div>
                    )}
                </>
            )}

            <div className="flex justify-end mt-6">
                {setIsDrawerOpen && (
                    <button
                        className="btn btn-outline"
                        onClick={() => setIsDrawerOpen(false)}
                        disabled={isLoading}
                    >
                        {t('absenceType.close')}
                    </button>
                )}
            </div>
        </div>
    );

    return isEditing ? renderForm() : renderList();
};

export default AbsenceType;
